import React, { createContext, useContext, useReducer, useEffect } from "react";
import { loadFromStorage, saveToStorage } from "../utils/localStorage";

const CartContext = createContext(null);

const ACTIONS = {
  ADD: "ADD",
  REMOVE: "REMOVE",
  UPDATE_QTY: "UPDATE_QTY",
  CLEAR: "CLEAR",
  RESTORE: "RESTORE",
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.RESTORE:
      return action.payload;

    case ACTIONS.ADD: {
      const { product, quantity = 1 } = action.payload;
      const validQty = Math.max(1, Math.round(quantity));
      const existing = state.find((i) => i.id === product.id);
      if (existing) {
        return state.map((i) =>
          i.id === product.id
            ? { ...i, quantity: i.quantity + validQty }
            : i
        );
      }
      return [
        ...state,
        {
          id: product.id,
          slug: product.slug,
          title: product.title,
          price: product.price,
          compareAtPrice: product.compareAtPrice,
          image: product.images?.[0],
          sku: product.sku,
          quantity: validQty,
          stock: product.stock,
        },
      ];
    }

    case ACTIONS.REMOVE:
      return state.filter((i) => i.id !== action.payload);

    case ACTIONS.UPDATE_QTY: {
      const { id, quantity } = action.payload;
      const validQty = Math.max(1, Math.round(quantity));
      if (quantity < 1) return state.filter((i) => i.id !== id);
      return state.map((i) => (i.id === id ? { ...i, quantity: validQty } : i));
    }

    case ACTIONS.CLEAR:
      return [];

    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [items, dispatch] = useReducer(cartReducer, []);

  useEffect(() => {
    const saved = loadFromStorage("ep_cart", []);
    if (saved.length > 0) dispatch({ type: ACTIONS.RESTORE, payload: saved });
  }, []);

  useEffect(() => {
    saveToStorage("ep_cart", items);
  }, [items]);

  const addToCart = (product, quantity = 1) =>
    dispatch({ type: ACTIONS.ADD, payload: { product, quantity } });

  const removeFromCart = (id) => dispatch({ type: ACTIONS.REMOVE, payload: id });

  const updateQuantity = (id, quantity) =>
    dispatch({ type: ACTIONS.UPDATE_QTY, payload: { id, quantity } });

  const clearCart = () => dispatch({ type: ACTIONS.CLEAR });

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const savings = items.reduce(
    (sum, i) => sum + (i.compareAtPrice ? (i.compareAtPrice - i.price) * i.quantity : 0),
    0
  );

  return (
    <CartContext.Provider
      value={{ items, addToCart, removeFromCart, updateQuantity, clearCart, subtotal, totalItems, savings }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
